import { useTranslation } from 'next-i18next';
import toast from 'react-hot-toast';
import { CheckedAnswer, EventPollQuestion } from '../interfaces/interfaces';
import { fetchAndSetEventPoll, fetchAndSetMeetingPoll, setMeetingPoll } from '../reduxToolkit/slices/commonSlice';
import { setAnsweredPollQuestions } from '../reduxToolkit/slices/videoSlice';
import { useSelector, useDispatch } from '../reduxToolkit/typedRedux';
import {
  checkEventPollAnswer,
  checkEventPollAnswerUnregistered,
  createEventPoll,
  createEventPollAnswer,
  createMeetingPoll,
  deleteEventPoll,
  getPollsPerMeeting,
  updateEventPoll,
} from '../services/services';
import tinycolor from 'tinycolor2';

const usePoll = () => {
  const event = useSelector((state) => state.common.event ?? state.admin.event);
  const meeting = useSelector(({ video }) => video.meeting);
  const answeredMeetingQuestions = useSelector(({ video }) => video.answeredPollQuestions);
  const user = useSelector((state) => state.common.user);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  async function addPollQuestion(object: EventPollQuestion, type: 'stage' | 'meeting', onSuccess?: Function) {
    if (object.answers.length === 0) return;
    else {
      try {
        if (event && type === 'stage') {
          const ques: any = await createEventPoll(event?._id, object.content, !!object.isActive);
          object.answers.map(async (e: any) => {
            await createEventPollAnswer(ques?._id, e.answer, e.sortIndex);
          });
          dispatch(fetchAndSetEventPoll(event?._id));
          toast.success(t('poll:poll-question-added'));
          onSuccess?.();
        } else if (meeting && type === 'meeting') {
          const ques: any = await createMeetingPoll(meeting._id, object.content, !!object.isActive);
          object.answers.map(async (e: any) => {
            await createEventPollAnswer(ques?._id, e.answer, e.sortIndex);
          });
          onSuccess?.();
          dispatch(fetchAndSetMeetingPoll(meeting._id));
          toast.success(t('poll:poll-question-added'));
        }
      } catch (error) {
        console.error(error);
      }
    }
  }

  function sumVotes(poll: EventPollQuestion) {
    return poll.answers.reduce((a, value) => a + value.votes, 0);
  }

  async function answerPoll(
    question: string,
    checkedAnswers: CheckedAnswer[],
    onSuccess?: Function,
    disableSuccessToast?: boolean,
    allowUnregistered?: boolean
  ) {
    if (!event) return;
    try {
      if (checkedAnswers.length > 0) {
        const filteredAnswers = checkedAnswers.filter((answer) => answer.questionid === question).map((answer) => answer.answerid);
        if (allowUnregistered && !user) {
          await checkEventPollAnswerUnregistered(filteredAnswers, event?._id, question);
        } else {
          await checkEventPollAnswer(filteredAnswers, event?._id, question);
        }
        (!disableSuccessToast || meeting) && toast.success(t('poll:poll-submitted'));
        meeting && dispatch(setAnsweredPollQuestions([...answeredMeetingQuestions!, question]));
        onSuccess?.();
      } else {
        toast(t('poll:info-polls'));
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function updatePoll(questionId: string, content?: string, isActive?: boolean, isShared?: boolean, allowMultiSelect?: boolean) {
    try {
      if (event) {
        await updateEventPoll(questionId, content, isActive, isShared, undefined, undefined, undefined, allowMultiSelect);
        if (!meeting?._id) dispatch(fetchAndSetEventPoll(event?._id));
        else dispatch(fetchAndSetMeetingPoll(meeting?._id));
        toast.success(t('poll:poll-question-updated'));
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function deletePoll(questionId: string) {
    try {
      if (event) {
        await deleteEventPoll(questionId);
        if (!meeting?._id) dispatch(fetchAndSetEventPoll(event?._id));
        else dispatch(fetchAndSetMeetingPoll(meeting?._id));
        toast.success(t('poll:poll-question-deleted'));
      }
    } catch (error) {
      console.error(error);
    }
  }

  const getPollTextColor = (disabled?: boolean, textColor?: string) => {
    let _textColor;
    if (textColor) _textColor = textColor;
    else if (event?.template?.mainButtonTextColor) _textColor = event?.template?.mainButtonTextColor;
    else _textColor = '#1e293b';
    return disabled
      ? tinycolor(_textColor).isLight()
        ? tinycolor(_textColor).darken(10).toString()
        : tinycolor(_textColor).lighten(10).toString()
      : _textColor;
  };

  const getPollBackgroundColor = (disabled?: boolean, backgroundColor?: string) => {
    let _backgroundColor;
    if (backgroundColor) _backgroundColor = backgroundColor;
    else if (event?.template?.mainColor) _backgroundColor = event?.template?.mainColor;
    else _backgroundColor = '#94a3b8';
    return disabled
      ? tinycolor(_backgroundColor).isLight()
        ? tinycolor(_backgroundColor).darken(10).toString()
        : tinycolor(_backgroundColor).lighten(10).toString()
      : _backgroundColor;
  };

  return { addPollQuestion, answerPoll, updatePoll, deletePoll, sumVotes, getPollTextColor, getPollBackgroundColor };
};
export default usePoll;
